import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { TopBar } from "@/components/Nav";
import { Confetti, ProgressBar, playCorrect, playWrong, Feedback } from "@/components/Bits";
import { recordAnswer, completeActivity } from "@/lib/progress";
import { speak } from "@/lib/speech";

function shuffle(a) { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; }

function DoneScreen({ title, backTo, emoji, text, onAgain }) {
  const nav = useNavigate();
  return (
    <>
      <TopBar title={title} backTo={backTo} />
      <div className="ll-bg min-h-screen flex items-center justify-center p-4">
        <Confetti show />
        <div className="ll-card p-8 text-center max-w-md ll-pop" data-testid="done-card">
          <div className="text-6xl">{emoji}</div>
          <h2 className="font-display font-bold text-3xl text-purple-900 mt-2">{text}</h2>
          <button onClick={onAgain} data-testid="play-again-button" className="ll-btn ll-btn-primary mt-4">Play again</button>
          <button onClick={() => nav(backTo)} data-testid="back-to-play-button" className="ll-btn ll-btn-secondary mt-3 block mx-auto">Back</button>
        </div>
      </div>
    </>
  );
}

/* Generic tap-to-answer puzzle runner (used by Pattern Play & Shadow Match). */
function PuzzleGame({ title, emoji, doneText, gameKey, question, rounds, renderPrompt, renderOption, cols = 3 }) {
  const [idx, setIdx] = useState(0);
  const [state, setState] = useState(null);
  const [sel, setSel] = useState(null);
  const [done, setDone] = useState(false);
  const round = rounds[idx];
  const options = React.useMemo(() => shuffle(round.options), [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { speak(question); }, [idx]); // eslint-disable-line react-hooks/exhaustive-deps

  const choose = (value) => {
    if (state === "correct") return;
    setSel(value);
    const ok = value === round.answer;
    recordAnswer("games", ok);
    if (ok) { playCorrect(); setState("correct"); } else { playWrong(); setState("wrong"); }
  };
  const next = () => {
    setState(null); setSel(null);
    if (idx + 1 >= rounds.length) { completeActivity("games", gameKey); setDone(true); }
    else setIdx(idx + 1);
  };
  const restart = () => { setDone(false); setIdx(0); setState(null); setSel(null); };

  if (done) return <DoneScreen title={title} backTo="/play" emoji={emoji} text={doneText} onAgain={restart} />;

  const colCls = { 3: "grid-cols-3", 4: "grid-cols-2 sm:grid-cols-4" }[cols] || "grid-cols-3";
  return (
    <>
      <TopBar title={title} backTo="/play" />
      <div className="ll-bg min-h-screen pb-32">
        <div className="max-w-3xl mx-auto px-4 pt-4">
          <ProgressBar percent={Math.round((idx / rounds.length) * 100)} />
          <div className="ll-card p-6 mt-4 text-center">
            <div className="font-display text-2xl sm:text-3xl font-bold text-purple-900">{question}</div>
            <div className="mt-6">{renderPrompt(round)}</div>
            <div className={`grid ${colCls} gap-3 sm:gap-4 mt-6`}>
              {options.map((o, i) => {
                const isSel = sel === o;
                const rightSel = state === "correct" && isSel;
                const wrongSel = state === "wrong" && isSel;
                return (
                  <button key={i} data-testid={`option-${i}`} onClick={() => choose(o)}
                    className={`ll-tile bg-white flex items-center justify-center min-h-[110px] ${rightSel ? "ring-4 ring-emerald-400 bg-emerald-50" : ""} ${wrongSel ? "ring-4 ring-pink-300 ll-shake" : ""}`}>
                    {renderOption(o)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <Feedback state={state} onNext={next} onRetry={() => { setState(null); setSel(null); }} />
        <Confetti show={state === "correct"} />
      </div>
    </>
  );
}

/* ---------- 1) Pattern Play — "What comes next?" ---------- */
const PATTERN_ROUNDS = [
  { seq: ["🔴", "🔵", "🔴", "🔵", "🔴"], answer: "🔵", options: ["🔵", "🟡", "🟢"] },
  { seq: ["🐶", "🐱", "🐶", "🐱"], answer: "🐶", options: ["🐶", "🐰", "🐸"] },
  { seq: ["⭐", "⭐", "🌙", "⭐", "⭐", "🌙", "⭐", "⭐"], answer: "🌙", options: ["🌙", "☀️", "⭐"] },
  { seq: ["🍎", "🍌", "🍎", "🍌", "🍎"], answer: "🍌", options: ["🍇", "🍌", "🍓"] },
  { seq: ["🔺", "🟦", "🟦", "🔺", "🟦", "🟦", "🔺"], answer: "🟦", options: ["🟦", "🔺", "🟩"] },
];
export function PatternGame() {
  return (
    <PuzzleGame title="Pattern Play" emoji="🧩" doneText="Pattern pro!" gameKey="pattern" question="What comes next?"
      rounds={PATTERN_ROUNDS} cols={3}
      renderPrompt={(r) => (
        <div className="flex flex-wrap items-center justify-center gap-2 p-4 rounded-3xl bg-violet-100 min-h-[110px]" data-testid="pattern-sequence">
          {r.seq.map((e, i) => <span key={i} className="text-5xl sm:text-6xl">{e}</span>)}
          <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white border-4 border-dashed border-purple-300 flex items-center justify-center text-4xl font-display font-bold text-purple-400">?</span>
        </div>
      )}
      renderOption={(o) => <span className="text-5xl sm:text-6xl">{o}</span>}
    />
  );
}

/* ---------- 2) Shadow Match — match the object to its shadow ---------- */
const SHADOW_ROUNDS = [
  { target: "🐘", answer: "🐘", options: ["🐘", "🦒", "🐢"] },
  { target: "✈️", answer: "✈️", options: ["🚗", "✈️", "⛵"] },
  { target: "🌳", answer: "🌳", options: ["🌵", "🌳", "🍄"] },
  { target: "🦆", answer: "🦆", options: ["🐟", "🦆", "🐝"] },
  { target: "🚀", answer: "🚀", options: ["🚀", "🏠", "⚽"] },
  { target: "🦋", answer: "🦋", options: ["🐌", "🐞", "🦋"] },
];
export function ShadowGame() {
  return (
    <PuzzleGame title="Shadow Match" emoji="🌚" doneText="Great eyes!" gameKey="shadow" question="Which shadow matches?"
      rounds={SHADOW_ROUNDS} cols={3}
      renderPrompt={(r) => (
        <div className="flex items-center justify-center p-4 rounded-3xl bg-amber-100 min-h-[130px]" data-testid="shadow-target">
          <span className="text-7xl sm:text-8xl ll-float">{r.target}</span>
        </div>
      )}
      renderOption={(o) => <span className="text-6xl sm:text-7xl" style={{ filter: "brightness(0)", opacity: 0.82 }}>{o}</span>}
    />
  );
}

/* ---------- 3) Copycat — Simon-says memory challenge ---------- */
const PADS = [
  { c: "#EF4444", lit: "#FCA5A5", name: "red" },
  { c: "#3B82F6", lit: "#93C5FD", name: "blue" },
  { c: "#22C55E", lit: "#86EFAC", name: "green" },
  { c: "#FACC15", lit: "#FEF08A", name: "yellow" },
];
const WIN_LEN = 4;
export function CopycatGame() {
  const [seq, setSeq] = useState([]);
  const [phase, setPhase] = useState("idle"); // idle | watch | input | done
  const [step, setStep] = useState(0);
  const [flash, setFlash] = useState(null);
  const [oops, setOops] = useState(false);
  const timers = useRef([]);
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => () => clearTimers(), []);

  const rand = () => Math.floor(Math.random() * 4);
  const startGame = () => { clearTimers(); setOops(false); setStep(0); setSeq([rand()]); setPhase("watch"); };

  // Play back the sequence whenever we (re)enter the watch phase.
  useEffect(() => {
    if (phase !== "watch" || seq.length === 0) return;
    clearTimers();
    let i = 0;
    setFlash(null);
    const playNext = () => {
      if (i >= seq.length) { const t = setTimeout(() => { setPhase("input"); setStep(0); }, 300); timers.current.push(t); return; }
      const pad = seq[i];
      setFlash(pad);
      const t1 = setTimeout(() => { setFlash(null); i += 1; const t2 = setTimeout(playNext, 240); timers.current.push(t2); }, 520);
      timers.current.push(t1);
    };
    const t0 = setTimeout(playNext, 550); timers.current.push(t0);
    return clearTimers;
  }, [phase, seq]);

  const tap = (pad) => {
    if (phase !== "input") return;
    setFlash(pad); const tf = setTimeout(() => setFlash(null), 180); timers.current.push(tf);
    if (pad === seq[step]) {
      const ns = step + 1;
      if (ns === seq.length) {
        recordAnswer("games", true); playCorrect();
        if (seq.length >= WIN_LEN) { completeActivity("games", "copycat"); setPhase("done"); }
        else { const t = setTimeout(() => { setSeq((s) => [...s, rand()]); setStep(0); setPhase("watch"); }, 750); timers.current.push(t); }
      } else setStep(ns);
    } else {
      recordAnswer("games", false); playWrong(); setOops(true); setPhase("idle");
    }
  };

  const restart = () => { clearTimers(); setSeq([]); setStep(0); setFlash(null); setOops(false); setPhase("idle"); };

  if (phase === "done") return <DoneScreen title="Copycat" backTo="/play" emoji="🧠" text="Amazing memory!" onAgain={restart} />;

  const status = phase === "watch" ? "Watch carefully…" : phase === "input" ? "Your turn — copy it!" : oops ? "Oops! Watch and try again 💜" : "Watch the colors, then copy them!";
  return (
    <>
      <TopBar title="Copycat" backTo="/play" />
      <div className="ll-bg min-h-screen pb-24">
        <div className="max-w-md mx-auto px-4 pt-4">
          <div className="ll-card p-3 text-center font-bold text-purple-700" data-testid="copycat-round">Round {Math.max(1, seq.length)} of {WIN_LEN}</div>
          <div className="ll-card p-6 mt-4 text-center">
            <div className="font-display text-2xl font-bold text-purple-900" data-testid="copycat-status">{status}</div>
            <div className="grid grid-cols-2 gap-4 mt-6" data-testid="copycat-pads">
              {PADS.map((p, i) => (
                <button key={i} data-testid={`pad-${p.name}`} disabled={phase === "watch"} onClick={() => tap(i)}
                  className="rounded-3xl aspect-square transition-all duration-150"
                  style={{ background: flash === i ? p.lit : p.c, boxShadow: flash === i ? `0 0 40px 6px ${p.lit}` : `0 8px 0 0 ${p.c}99`, transform: flash === i ? "scale(1.04)" : "scale(1)" }} />
              ))}
            </div>
            {phase === "idle" && (
              <button onClick={startGame} data-testid="copycat-start" className="ll-btn ll-btn-primary mt-6 w-full text-lg">{oops ? "Try again" : "Start"} ▶</button>
            )}
            {phase === "input" && (
              <button onClick={() => setPhase("watch")} data-testid="copycat-watch-again" className="ll-btn ll-btn-secondary mt-6">Watch again 👀</button>
            )}
          </div>
        </div>
        <Confetti show={phase === "done"} />
      </div>
    </>
  );
}

/* Which is different - odd one out */
const DIFF = [
  { items: ["🍎", "🍎", "🍎", "🍌"], correct: 3 },
  { items: ["⭐", "⭐", "🌙", "⭐"], correct: 2 },
  { items: ["🐶", "🐶", "🐶", "🐱"], correct: 3 },
  { items: ["🔴", "🔴", "🔵", "🔴"], correct: 2 },
];

export function DifferentGame() {
  const nav = useNavigate();
  const [idx, setIdx] = useState(0);
  const [state, setState] = useState(null);
  const [done, setDone] = useState(false);
  const item = DIFF[idx];
  const choose = (i) => {
    if (state === "correct") return;
    const ok = i === item.correct;
    recordAnswer("games", ok);
    if (ok) { playCorrect("Different"); setState("correct"); }
    else { playWrong(); setState("wrong"); }
  };
  const next = () => { setState(null); if (idx + 1 >= DIFF.length) { completeActivity("games", "different"); setDone(true); } else setIdx(idx + 1); };

  if (done) return (
    <>
      <TopBar title="Which is Different?" backTo="/play" />
      <div className="ll-bg min-h-screen flex items-center justify-center p-4">
        <Confetti show />
        <div className="ll-card p-8 text-center max-w-md ll-pop" data-testid="done-card">
          <div className="text-6xl">✨</div>
          <h2 className="font-display font-bold text-3xl text-purple-900 mt-2">Sharp eyes!</h2>
          <button onClick={() => { setDone(false); setIdx(0); }} data-testid="play-again-button" className="ll-btn ll-btn-primary mt-4">Play again</button>
          <button onClick={() => nav("/play")} data-testid="back-to-play-button" className="ll-btn ll-btn-secondary mt-3 block mx-auto">Back</button>
        </div>
      </div>
    </>
  );
  return (
    <>
      <TopBar title="Which is Different?" backTo="/play" />
      <div className="ll-bg min-h-screen pb-32">
        <div className="max-w-3xl mx-auto px-4 pt-4">
          <ProgressBar percent={Math.round((idx / DIFF.length) * 100)} />
          <div className="ll-card p-6 mt-4 text-center">
            <div className="font-display text-2xl font-bold text-purple-900">Which one is different?</div>
            <div className="grid grid-cols-4 gap-3 mt-6">
              {item.items.map((e, i) => (
                <button key={i} data-testid={`option-${i}`} onClick={() => choose(i)} className="ll-tile bg-white text-6xl min-h-[120px]">{e}</button>
              ))}
            </div>
          </div>
        </div>
        <Feedback state={state} onNext={next} onRetry={() => setState(null)} />
        <Confetti show={state === "correct"} />
      </div>
    </>
  );
}
